import React, { useState, useMemo } from 'react';
import { Table, Button, Input, Alert, message, Card } from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { SpaceAssignmentTable } from './SpaceAssignmentTable';
import { InviteUserModal } from './InviteUserModal';
import { AssignSpaceAfterRoleChangeModal, AuthApiRole } from './AssignSpaceAfterRoleChangeModal';
import { TransferOwnershipModal } from './TransferOwnershipModal';
import { DeleteUserModal } from './DeleteUserModal';
import { formatOperationError } from '@/api/formatError';
import { useAuthUser } from '@/hooks/useAuth';
import { useSpaceAccess } from '@/contexts/SpaceAccessContext';
import { useTimezone } from '@/hooks/useTimezone';
import { useUsersData, UserTableRecord, EditingData } from './useUsersData';
import { getUserColumns } from './getUserColumns';

// `roleChange` carries the pending Admin/Owner→Member downgrade: the role
// mutation is deferred until the modal has at least one space selected, so
// cancelling is a true abort rather than leaving a zero-access user.
// `originalRoles` lets the modal roll the role back if every space assignment
// fails after the role commit.
type ActiveModal =
  | null
  | { kind: 'invite' }
  | {
      kind: 'roleChange';
      userId: string;
      rolesToAssign: AuthApiRole[];
      originalRoles: AuthApiRole[];
    }
  | { kind: 'transferOwnership'; userId: string; userName: string }
  | { kind: 'delete'; userId: string; userName: string };

export const UserManagement = () => {
  const { isSuperUser } = useAuthUser();
  const { isAuthApiAdmin, isAuthApiOwner, currentUserId } = useSpaceAccess();
  const timezone = useTimezone();

  const {
    tableData,
    hiddenSuperuserCount,
    isLoading,
    error,
    refetch,
    updateUser,
    isUpdating,
    deleteUser,
  } = useUsersData();

  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<EditingData>({ name: '', role: '' });
  const [searchText, setSearchText] = useState<string>('');
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  const filteredTableData = useMemo(() => {
    if (!searchText.trim()) return tableData;
    const lowerSearch = searchText.toLowerCase();
    return tableData.filter(
      (user) =>
        user.name?.toLowerCase().includes(lowerSearch) ||
        user.email?.toLowerCase().includes(lowerSearch) ||
        user.role?.toLowerCase().includes(lowerSearch),
    );
  }, [tableData, searchText]);

  const handleRefresh = async () => {
    try {
      await refetch();
      message.success('User list refreshed');
    } catch (err) {
      console.error('Failed to refresh user list:', err);
      message.error(formatOperationError('refresh user list', err));
    }
  };

  const handleExport = () => {
    message.info('Export functionality coming soon');
  };

  const handleEditUser = (userId: string, name: string, role: string) => {
    setEditingUserId(userId);
    setEditingData({ name, role });
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditingData({ name: '', role: '' });
  };

  // Map the display role from the table row back to backend role names. The
  // `['butlr_user']` fallback is safe-by-design: it is only reachable if the
  // row was deleted between edit-open and Save, in which case
  // `originalRoles === ['butlr_user']` makes the rollback a no-op rather than
  // escalating the user to a higher role.
  const toApiRoles = (display: string | undefined): AuthApiRole[] => {
    if (display === 'Owner') return ['owner'];
    if (display === 'Admin') return ['admin'];
    return ['butlr_user'];
  };

  const handleSaveEdit = async (userId: string) => {
    const originalUser = tableData.find((u) => u.id === userId);
    const wasAdminOrOwner = originalUser?.role === 'Admin' || originalUser?.role === 'Owner';
    const isNowMember = editingData.role === 'Member';
    const isRoleDowngrade = wasAdminOrOwner && isNowMember;

    const roles = toApiRoles(editingData.role);
    const originalRoles = toApiRoles(originalUser?.role);

    // For a downgrade we don't commit the role yet — open the post-downgrade
    // modal, which forces at least one space selection before the role
    // mutation lands. Cancelling the modal aborts the downgrade.
    if (isRoleDowngrade) {
      setActiveModal({ kind: 'roleChange', userId, rolesToAssign: roles, originalRoles });
      return;
    }

    try {
      await updateUser({
        variables: {
          id: userId,
          input: { roles },
        },
      });
      message.success('User updated successfully');
      setEditingUserId(null);
      setEditingData({ name: '', role: '' });
      refetch();
    } catch (err) {
      console.error('Failed to update user:', err);
      message.error(formatOperationError('update user', err));
    }
  };

  const handleDeleteUser = async () => {
    if (activeModal?.kind !== 'delete') return;
    const { userId } = activeModal;

    setDeletingUserId(userId);
    try {
      await deleteUser({ variables: { id: userId } });
      message.success('User deleted successfully');
      setActiveModal(null);
      refetch();
    } catch (err) {
      console.error('Failed to delete user:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete user';
      message.error(errorMessage);
    } finally {
      setDeletingUserId(null);
    }
  };

  const columns = getUserColumns({
    editingUserId,
    editingData,
    setEditingData,
    isSuperUser,
    isAuthApiAdmin,
    isAuthApiOwner,
    currentUserId,
    isUpdating,
    deletingUserId,
    timezone,
    onEditUser: handleEditUser,
    onSaveEdit: handleSaveEdit,
    onCancelEdit: handleCancelEdit,
    onRequestDelete: (target) => setActiveModal({ kind: 'delete', ...target }),
    onRequestTransferOwnership: (target) =>
      setActiveModal({ kind: 'transferOwnership', ...target }),
  });

  return (
    <div className="bg-surface-sunken w-full min-h-screen p-6">
      <Card>
        <div className="flex justify-between items-center mb-4">
          <div className="font-semibold text-base">Team Members ({filteredTableData.length})</div>
          <div className="flex items-center gap-3">
            <Input
              placeholder="Search users..."
              prefix={<SearchOutlined className="text-content-tertiary" />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 240 }}
              allowClear
            />
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={() => setActiveModal({ kind: 'invite' })}
              className="!bg-btn-action-bg !text-btn-action-text hover:!bg-btn-action-hover !border-btn-action-bg"
            >
              Invite
            </Button>
            <Button type="text" icon={<DownloadOutlined />} onClick={handleExport} title="Export" />
            <Button type="text" icon={<ReloadOutlined />} onClick={handleRefresh} title="Refresh" />
          </div>
        </div>

        {error && (
          <Alert
            message="Error loading users"
            description="Failed to fetch users from the server. Please try again."
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          />
        )}
        {hiddenSuperuserCount > 0 && (
          <Alert
            message={
              hiddenSuperuserCount === 1
                ? '1 user hidden (superuser role) — contact Butlr support to manage.'
                : `${hiddenSuperuserCount} users hidden (superuser role) — contact Butlr support to manage.`
            }
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <Table
          columns={columns}
          dataSource={filteredTableData}
          loading={isLoading}
          showSorterTooltip={false}
          expandable={{
            expandedRowRender: (record: UserTableRecord) => (
              <SpaceAssignmentTable
                userId={record.id}
                userRole={record.role}
                onUpdate={handleRefresh}
              />
            ),
            rowExpandable: (record: UserTableRecord) =>
              record.role !== 'Admin' && record.role !== 'Owner' && record.role !== 'Superuser',
          }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
          }}
        />
      </Card>

      <InviteUserModal
        open={activeModal?.kind === 'invite'}
        onCancel={() => setActiveModal(null)}
        onSuccess={() => {
          setActiveModal(null);
          handleRefresh();
        }}
      />

      {activeModal?.kind === 'roleChange' && (
        <AssignSpaceAfterRoleChangeModal
          open
          userId={activeModal.userId}
          rolesToAssign={activeModal.rolesToAssign}
          originalRoles={activeModal.originalRoles}
          onCancel={() => {
            // Abort the downgrade — leave editing state intact so the admin
            // can re-confirm or change their mind.
            setActiveModal(null);
          }}
          onSuccess={() => {
            setActiveModal(null);
            setEditingUserId(null);
            setEditingData({ name: '', role: '' });
            handleRefresh();
          }}
        />
      )}

      {activeModal?.kind === 'transferOwnership' && (
        <TransferOwnershipModal
          open
          userId={activeModal.userId}
          userName={activeModal.userName}
          onCancel={() => setActiveModal(null)}
          onSuccess={() => {
            setActiveModal(null);
            handleRefresh();
          }}
        />
      )}

      {activeModal?.kind === 'delete' && (
        <DeleteUserModal
          open
          userName={activeModal.userName}
          loading={deletingUserId === activeModal.userId}
          onCancel={() => setActiveModal(null)}
          onConfirm={handleDeleteUser}
        />
      )}
    </div>
  );
};

export default UserManagement;
